import React, { useContext, useState, useCallback, useEffect } from 'react';
import { Typography, Col, Row, Tag, message, Button, Drawer, Badge } from 'antd';
import { SafetyOutlined, HistoryOutlined, CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  TheftProtectionAlert,
  VehicleDamageAlert,
  EmergencyContactAlert,
  TowingServiceAlert,
} from './SafetyBlocks';

const { Title, Text } = Typography;

// ─── Relative time helper ─────────────────────────────────────────────────────
function relativeTime(ts) {
  const diff = Date.now() - ts;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

// ─── History entry row ────────────────────────────────────────────────────────
const SafetyHistoryRow = ({ entry, index }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      padding: '10px 0',
      borderBottom: '1px solid #f1f5f9',
      animation: `safety-hist-in 0.3s ease both ${index * 0.04}s`,
    }}
  >
    {/* status dot */}
    <div
      style={{
        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
        background: entry.success ? '#16a34a' : '#ef4444',
        marginTop: 5,
      }}
    />

    {/* content */}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>
        {entry.name}
      </div>
      {entry.value && (
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{entry.value}</div>
      )}
    </div>

    {/* right: time + badge */}
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
      <span style={{ fontSize: 10, color: '#94a3b8' }}>{relativeTime(entry.timestamp)}</span>
      <Tag
        style={{
          margin: 0, borderRadius: 20, fontSize: 9, fontWeight: 700, padding: '0 7px',
          background: entry.success ? '#f0fdf4' : '#fff5f5',
          border: `1px solid ${entry.success ? '#86efac' : '#fca5a5'}`,
          color: entry.success ? '#16a34a' : '#ef4444',
        }}
      >
        {entry.success
          ? <><CheckCircleOutlined /> Sent</>
          : <><CloseCircleOutlined /> Failed</>}
      </Tag>
    </div>
  </div>
);

// ─── Empty history state ──────────────────────────────────────────────────────
const EmptySafetyHistory = () => (
  <div
    style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', gap: 10, padding: '60px 24px',
    }}
  >
    <div
      style={{
        width: 52, height: 52, borderRadius: 13,
        background: '#f1f5f9', border: '1px solid #e2e8f0',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 26, color: '#cbd5e1',
      }}
    >
      <HistoryOutlined />
    </div>
    <Text style={{ fontSize: 13, color: '#94a3b8' }}>No safety alerts triggered yet</Text>
    <Text style={{ fontSize: 11, color: '#cbd5e1', textAlign: 'center' }}>
      Trigger a safety alert to see it recorded here
    </Text>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const Safety = () => {
  const [messageApi, contextHolder] = message.useMessage();

  const [safetyHistory, setSafetyHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem('safety_history') || '[]'); } catch { return []; }
  });
  const [historyOpen, setHistoryOpen] = useState(false);

  // Inject CSS once
  useEffect(() => {
    let el = document.getElementById('safety-hist-animations');
    if (!el) { el = document.createElement('style'); el.id = 'safety-hist-animations'; document.head.appendChild(el); }
    el.textContent = `
      @keyframes safety-hist-in {
        from { opacity: 0; transform: translateX(10px); }
        to   { opacity: 1; transform: translateX(0); }
      }
    `;
    return () => { document.getElementById('safety-hist-animations')?.remove(); };
  }, []);

  const addToHistory = useCallback((record) => {
    const entry = { ...record, timestamp: Date.now(), id: `${Date.now()}-${Math.random()}` };
    setSafetyHistory((prev) => {
      const next = [entry, ...prev].slice(0, 100);
      localStorage.setItem('safety_history', JSON.stringify(next));
      return next;
    });
  }, []);

  const clearHistory = () => {
    setSafetyHistory([]);
    localStorage.removeItem('safety_history');
  };

  const successCount = safetyHistory.filter((e) => e.success).length;
  const failCount = safetyHistory.length - successCount;

  return (
    <>
      {contextHolder}

      {/* ── Page header ────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 20, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 38, height: 38, borderRadius: 10,
              background: '#eff6ff', border: '1px solid #bfdbfe',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            <SafetyOutlined style={{ fontSize: 18, color: '#2563eb' }} />
          </div>
          <div>
            <Title level={4} style={{ margin: 0 }}>Safety & Protection</Title>
            <Text style={{ fontSize: 12, color: '#94a3b8' }}>Vehicle security, emergency alerts, and roadside assistance</Text>
          </div>
        </div>

        {/* right: history button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Badge count={safetyHistory.length} size="small" offset={[-4, 4]}>
            <Button
              icon={<HistoryOutlined />}
              onClick={() => setHistoryOpen(true)}
              style={{ borderRadius: 9 }}
            >
              History
            </Button>
          </Badge>
        </div>
      </div>

      {/* ── Safety alert cards grid ─────────────────────────────────────────────── */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8} xl={6} style={{ display: 'flex' }}>
          <div style={{ width: '100%' }}>
            <TheftProtectionAlert
              messageApi={messageApi}
              onTriggered={(record) => addToHistory({ name: 'Theft Protection', ...record })}
            />
          </div>
        </Col>

        <Col xs={24} sm={12} lg={8} xl={6} style={{ display: 'flex' }}>
          <div style={{ width: '100%' }}>
            <VehicleDamageAlert
              messageApi={messageApi}
              onTriggered={(record) => addToHistory({ name: 'Vehicle Damage', ...record })}
            />
          </div>
        </Col>

        <Col xs={24} sm={12} lg={8} xl={6} style={{ display: 'flex' }}>
          <div style={{ width: '100%' }}>
            <EmergencyContactAlert
              messageApi={messageApi}
              onTriggered={(record) => addToHistory({ name: 'Emergency Alert', ...record })}
            />
          </div>
        </Col>

        <Col xs={24} sm={12} lg={8} xl={6} style={{ display: 'flex' }}>
          <div style={{ width: '100%' }}>
            <TowingServiceAlert
              messageApi={messageApi}
              onTriggered={(record) => addToHistory({ name: 'Towing Service', ...record })}
            />
          </div>
        </Col>
      </Row>

      {/* ── Safety History Drawer ──────────────────────────────────────────────── */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <HistoryOutlined style={{ color: '#64748b' }} />
            <span style={{ fontWeight: 700 }}>Safety History</span>
            {safetyHistory.length > 0 && (
              <div style={{ display: 'flex', gap: 6, marginLeft: 4 }}>
                <Tag style={{ margin: 0, borderRadius: 20, fontSize: 9, fontWeight: 700, background: '#f0fdf4', border: '1px solid #86efac', color: '#16a34a' }}>
                  {successCount} sent
                </Tag>
                {failCount > 0 && (
                  <Tag style={{ margin: 0, borderRadius: 20, fontSize: 9, fontWeight: 700, background: '#fff5f5', border: '1px solid #fca5a5', color: '#ef4444' }}>
                    {failCount} failed
                  </Tag>
                )}
              </div>
            )}
          </div>
        }
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        width={400}
        footer={
          safetyHistory.length > 0 ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                onClick={clearHistory}
                style={{ borderRadius: 8 }}
              >
                Clear All
              </Button>
            </div>
          ) : null
        }
        styles={{ body: { padding: '0 20px' } }}
      >
        {safetyHistory.length === 0 ? (
          <EmptySafetyHistory />
        ) : (
          <div>
            {safetyHistory.map((entry, i) => (
              <SafetyHistoryRow key={entry.id} entry={entry} index={i} />
            ))}
          </div>
        )}
      </Drawer>
    </>
  );
};

export default Safety;